import React from 'react';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';
import type { AnnounceOptions } from './WidgetAnnouncerProvider';

interface UseMessageFeedbackDisclosureOptions {
  rating: 'up' | 'down' | null;
  timestamp: number;
  announce: (message: string, options?: AnnounceOptions) => void;
}

/**
 * Opens the thumbs-down comment card, moves focus into it, and restores
 * focus to the toggle after submit so the live-region announcement is
 * also reachable in browse mode.
 */
export function useMessageFeedbackDisclosure({
  rating,
  timestamp,
  announce,
}: UseMessageFeedbackDisclosureOptions) {
  const { t, locale } = useWidgetI18n();
  const [comment, setComment] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);
  const [commentBoxOpen, setCommentBoxOpen] = React.useState(false);
  const commentRef = React.useRef<HTMLTextAreaElement | null>(null);
  const notHelpfulRef = React.useRef<HTMLButtonElement | null>(null);
  const prevRatingRef = React.useRef(rating);
  // Incremented only on a fresh thumbs-down so remounts with a persisted
  // rating do not pull focus out of the composer.
  const [focusCommentNonce, setFocusCommentNonce] = React.useState(0);
  const commentId = `feedback-comment-${timestamp}`;
  const isCommentFormOpen = commentBoxOpen && !submitted;

  React.useEffect(() => {
    if (rating === 'down') {
      setCommentBoxOpen(true);
      setSubmitted(false);
      if (prevRatingRef.current !== 'down') {
        setFocusCommentNonce((n) => n + 1);
        announce(t('feedback.markedNotHelpful'), { lang: locale });
      }
    } else {
      setComment('');
      setSubmitted(false);
      setCommentBoxOpen(false);
    }
    prevRatingRef.current = rating;
  }, [rating, announce, t, locale]);

  // Focus after the comment field is committed to the DOM. queueMicrotask on
  // the rating change ran before the textarea existed, so VoiceOver stayed on
  // the composer.
  React.useEffect(() => {
    if (!isCommentFormOpen || focusCommentNonce === 0) return;
    const frame = requestAnimationFrame(() => commentRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [isCommentFormOpen, focusCommentNonce]);

  React.useEffect(() => {
    if (!submitted) return;
    // TrapFocus on the chat Paper restores focus to the dialog when Submit
    // unmounts. That runs in useEffect, after queueMicrotask, so two frames
    // are needed to land on Not helpful instead of the MuiPaper.
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => notHelpfulRef.current?.focus());
    });
    const timer = setTimeout(() => {
      setSubmitted(false);
      setCommentBoxOpen(false);
    }, 2000);
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
      clearTimeout(timer);
    };
  }, [submitted]);

  const markSubmitted = React.useCallback(() => {
    setComment('');
    setSubmitted(true);
    announce(t('feedback.thankYou'), { lang: locale });
  }, [announce, t, locale]);

  return {
    comment,
    setComment,
    submitted,
    commentBoxOpen,
    isCommentFormOpen,
    commentId,
    commentRef,
    notHelpfulRef,
    markSubmitted,
  };
}
